import { type FC, type ReactElement, type RefObject, useEffect, useRef } from "react"

import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import { type Location, type NavigateFunction, useLocation, useNavigate } from "react-router-dom"
import { useUpdate } from "react-use"
import sanitizeHtml from "sanitize-html"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { EPath } from "@/3514/constants"
import { type TScreenBreakpoints, type TUserState, useScreenBreakpoints, useUser } from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import useWaitingListText from "@/api/use-waiting-list-text"
import { CARE_TEAM_EMAIL, JS_CONF, ParticipantUrls } from "@/constants"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { SimpleLayout } from "@/layouts"
import type { TEmptyCallback } from "@/shared/types/functions"

import WaitingListHeader from "./WaitingListHeader"

import "./styles.scss"

interface IProps {}

const WaitingListPage: FC<IProps> = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()
    const navigate: NavigateFunction = useNavigate()
    const {
        user,
        isLoading,
        refetch: refetchUser
    }: TUserState<EUserRole.Participant> = useUser<EUserRole.Participant>()
    const { data }: ReturnType<typeof useWaitingListText> = useWaitingListText()
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()
    const location: Location = useLocation()

    useEffect((): void => {
        if (user.doesCohortExist) return void navigate(EPath.Participant_OnboardingRedirect(), { replace: true })

        const _isSsoPage: boolean = includes<string>(location.pathname, "unrecognized-sso")

        const _isSsoUser: boolean = Boolean(user.isUnexpectedAuth && !isEmpty(user?.organizationSlug))

        if (_isSsoPage) if (!_isSsoUser) return void void navigate(ParticipantUrls.WAITING_LIST)

        if (!_isSsoPage)
            if (_isSsoUser)
                return void navigate(`${ParticipantUrls.WAITING_LIST}/unrecognized-sso/${user?.organizationSlug}`)

        return void 0
    }, [navigate, user.doesCohortExist, user.isUnexpectedAuth, user.organizationSlug, location.pathname])

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    const wasReloaded: RefObject<boolean> = useRef<boolean>(false)

    const update: TEmptyCallback = useUpdate()

    useEffect((): void => {
        if (isLoading && !wasReloaded.current) {
            void refetchUser()
            wasReloaded.current = true
        }

        update()
    }, [isLoading])

    return (
        <SimpleLayout
            header={!isMobile ? <WaitingListHeader /> : null}
            showProfilePhoto
            showAvatarOnMobile
            withUserDropdownWithoutMargin
        >
            <div className="waiting-list__container text-center">
                <div className="waiting-list__content">
                    <Typography
                        text={user.isUnexpectedAuth ? t("Welcome, we weren’t expecting you!") : data?.textTitleData}
                        className="waiting-list__title"
                        tag={ETypographyTag.Heading1}
                        color={ETypographyColor.Black}
                        alignment={ETypographyAlignment.Center}
                        weight={ETypographyWeight.Medium}
                        size={ETypographySize.Heading1}
                        family={ETypographyFamily.DmSerif}
                    />

                    <p
                        className="waiting-list__text color-gray"
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                                user.isUnexpectedAuth
                                    ? t(
                                          "Click the button below to contact Wiser’s Care Team. Once you reach out to us, we’ll get in touch with your program’s sponsor to figure out why your account hasn’t been created in our system yet."
                                      )
                                    : data?.text,
                                { allowedTags: [] }
                            )
                        }}
                    />

                    {!user.isUnexpectedAuth ? (
                        <p className="m-2 fs-14 color-gray">
                            {data?.textSubtitleData}{" "}
                            <a href={`mailto:${data?.contactEmail}`} className="waiting-list__help-link fs-14">
                                {data?.textInfoEmailData}
                            </a>
                        </p>
                    ) : (
                        <div className="btn btn--brand">
                            <a href={CARE_TEAM_EMAIL} className="color-inherit">
                                {t("Contact Care")}
                            </a>
                        </div>
                    )}
                </div>
                <img
                    alt={t("Big blinking orange owl")}
                    src={`${JS_CONF?.static}img/owl-wiser.gif`}
                    className="waiting-list__image"
                />
            </div>
        </SimpleLayout>
    )
}

export default WaitingListPage
